import clsx from 'clsx';
import { ChangeEvent, ReactNode } from 'react';
import Breadcrumbs, { BreadcrumbItem } from './Breadcrumbs';
import SearchInput from './SearchInput';
import { usePageHeaderStyles } from './PageHeader.styles';

export type PageHeaderSearchProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  ariaLabel: string;
  placeholder?: string;
};

type PageHeaderProps = {
  breadcrumbs?: BreadcrumbItem[];
  onBack?: () => void;
  backLabel?: string;
  title: string;
  description: string;
  titleVariant?: 'default' | 'primary';
  descriptionVariant?: 'default' | 'wide' | 'brown';
  search?: PageHeaderSearchProps;
  content?: ReactNode;
  contentClassName?: string;
  actions?: ReactNode;
};

const PageHeader = ({
  breadcrumbs,
  onBack,
  backLabel = 'Retour',
  title,
  description,
  titleVariant = 'default',
  descriptionVariant = 'default',
  search,
  content,
  contentClassName,
  actions,
}: PageHeaderProps) => {
  const classes = usePageHeaderStyles();
  const hasHeaderActions = Boolean(search || actions || content);

  const titleElement = (
    <h1
      className={clsx(
        classes.pageTitle,
        titleVariant === 'primary' && classes.pageTitlePrimary,
      )}>
      {title}
    </h1>
  );

  const descriptionElement = (
    <p
      className={clsx(
        classes.pageDescription,
        descriptionVariant === 'wide' && classes.pageDescriptionWide,
        descriptionVariant === 'brown' && classes.pageDescriptionBrown,
      )}>
      {description}
    </p>
  );

  return (
    <div className={classes.headerBlock}>
      {onBack ? (
        <button type="button" className={classes.backButton} onClick={onBack}>
          ← {backLabel}
        </button>
      ) : (
        breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />
      )}
      {hasHeaderActions ? (
        <div className={classes.pageHeaderRow}>
          <div className={classes.pageHeaderText}>
            {titleElement}
            {descriptionElement}
            {content && (
              <div className={clsx(classes.pageHeaderContent, contentClassName)}>{content}</div>
            )}
          </div>
          <div className={classes.pageHeaderActions}>
            {actions}
            {search && (
              <SearchInput
                value={search.value}
                onChange={search.onChange}
                ariaLabel={search.ariaLabel}
                placeholder={search.placeholder}
                className={classes.headerSearch}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          {titleElement}
          {descriptionElement}
          {content && (
            <div className={clsx(classes.pageHeaderContent, contentClassName)}>{content}</div>
          )}
        </>
      )}
    </div>
  );
};

export default PageHeader;
